import { z } from "zod";

/**
 * A practice's own social profiles, stored as one versioned JSON blob on the Practice.
 *
 * The email footer renders these in place of Ultralight's. They are practice settings rather than
 * email settings — the practice enters them once under Settings -> Practice, and any surface that
 * wants to link a practice's socials reads the same field.
 *
 * Follows the same shape as `lib/messaging/routing.ts`: a Zod schema with a `version` literal, a
 * typed default, and a `parse*` helper that never throws.
 */

/** The networks a practice can publish. Ordered — the footer renders the icons in this order. */
export const PRACTICE_SOCIAL_NETWORKS = ["instagram", "linkedin", "twitter"] as const;

export type PracticeSocialNetwork = (typeof PRACTICE_SOCIAL_NETWORKS)[number];

/** Display name per network. The settings form labels and the footer's alt text both read it. */
export const PRACTICE_SOCIAL_NETWORK_LABELS: Record<PracticeSocialNetwork, string> = {
  instagram: "Instagram",
  linkedin: "LinkedIn",
  twitter: "X (Twitter)",
};

export const practiceSocialLinksSchema = z.object({
  /** Schema version so the stored blob can change shape without an ambiguous read. */
  version: z.literal(1).default(1),
  instagram: z.string().optional(),
  linkedin: z.string().optional(),
  twitter: z.string().optional(),
});

export type PracticeSocialLinks = z.infer<typeof practiceSocialLinksSchema>;

/** A practice that has never entered socials. The footer then falls back to Ultralight's. */
export const DEFAULT_PRACTICE_SOCIAL_LINKS: PracticeSocialLinks = { version: 1 };

/**
 * Normalizes a social profile URL the practice typed.
 *
 * A practice pastes `instagram.com/kyndred` as often as the full URL, so a missing scheme gets
 * `https://`. Only http(s) survives. This is narrower than the editor's `isAllowedHref`, which
 * also permits `mailto:`, `tel:`, and relative hrefs — none of those name a social profile, and
 * an email client renders them as a broken icon link.
 *
 * Returns `undefined` for anything unusable, so a caller can treat "blank" and "invalid" alike.
 */
export function normalizePracticeSocialUrl(input: string): string | undefined {
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
  const candidate = hasScheme ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    // A scheme and no host ("https://") parses, and links nowhere.
    if (!url.hostname) return undefined;
    return url.toString();
  } catch {
    return undefined;
  }
}

/** True when the input is blank, or a link this can store. Drives the settings form's validation. */
export function isStorablePracticeSocialUrl(input: string | undefined): boolean {
  if (!input?.trim()) return true;
  return normalizePracticeSocialUrl(input) !== undefined;
}

/**
 * Builds the stored blob from raw form input, normalizing each entry and dropping the unusable
 * ones. Normalizing on write means every reader gets a link an email client can follow.
 */
export function buildPracticeSocialLinks(
  input: Partial<Record<PracticeSocialNetwork, string | undefined>>,
): PracticeSocialLinks {
  const links: PracticeSocialLinks = { version: 1 };

  for (const network of PRACTICE_SOCIAL_NETWORKS) {
    const raw = input[network];
    const normalized = raw ? normalizePracticeSocialUrl(raw) : undefined;
    if (normalized) {
      links[network] = normalized;
    }
  }

  return links;
}

/**
 * Reads the stored blob. Falls back to the empty default on absent, unparseable, or invalid JSON,
 * so a malformed extension never breaks a send.
 */
export function parsePracticeSocialLinks(raw: string | undefined): PracticeSocialLinks {
  if (!raw) return DEFAULT_PRACTICE_SOCIAL_LINKS;

  try {
    const parsed = practiceSocialLinksSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : DEFAULT_PRACTICE_SOCIAL_LINKS;
  } catch {
    return DEFAULT_PRACTICE_SOCIAL_LINKS;
  }
}

/** The networks the practice actually filled in, in render order. Empty means "show none". */
export function listPracticeSocialLinks(
  links: PracticeSocialLinks,
): { network: PracticeSocialNetwork; label: string; url: string }[] {
  return PRACTICE_SOCIAL_NETWORKS.flatMap((network) => {
    const url = links[network];
    if (!url) return [];
    return [{ network, label: PRACTICE_SOCIAL_NETWORK_LABELS[network], url }];
  });
}
